(ns frontend.ui.component.lp-portal.report.body.real-estate.holding.property.controller
  (:require
   [frontend.ui.component.chart.utils :as chart-utils]
   [frontend.ui.component.lp-portal.report.controller :as report-ctl]
   [frontend.ui.component.lp-portal.report.tearsheet.portfolio-locations.controller
    :refer [portfolio-locations]]
   [frontend.ui.page.lp-portal.report.util :as util]
   [frontend.ui.style.theme.core :as theme]
   [frontend.ui.style.theme.core :refer [color]]))

(defn config->editable-data-noi
  [config]
  {:columns (some->> config
                     :xAxis
                     (mapcat :categories)
                     (vec))
   :data    (->> config
                 :series
                 (mapv (fn [serie]
                         (vec (concat [(:name serie)]
                                      (:data serie))))))})

(defn editable-data->raw-data-noi
  [edited-data]
  (merge {:labels (:columns edited-data)}
         (->> edited-data
              :data
              (keep (fn [row]
                      (let [row-key (first row)
                            number-data (->> (rest row)
                                             (mapv (fn [v]
                                                     (when (number? v) v))))]
                        (case row-key
                          "NOI"
                          [:noi number-data]
                          "Annualized net yield on FV"
                          [:annualized-net-yield-on-fv number-data]
                          (js/console.warn "Cannot resolve data " row)))))
              (into {}))))

(defn config->editable-data-investment-at-cost-valuation
  [config]
  {:columns (some->> config
                     :xAxis
                     :categories)
   :data    (->> config
                 :series
                 (mapv (fn [serie]
                         (vec (concat [(:name serie)]
                                      (:data serie))))))})

(defn editable-data->raw-data-investment-at-cost-valuation
  [edited-data]
  (let [data (->> edited-data
                  :data
                  (first)
                  (rest))]
    {:labels                (:columns edited-data)
     :initial-investment    (nth data 0)
     :at-cost               (nth data 1)
     :previous-valuation-q2 (nth data 2)
     :current-valuation     (nth data 3)}))

(defn- investment-at-cost-and-valuation
  [chart-data-valuation]
  (let [labels ["Initial investment"
                "At cost"
                "Valuation previous quarter"
                "Current valuation"]
        data (->> chart-data-valuation
                  ((juxt :initial-investment :at-cost :previous-valuation-q2 :current-valuation)))]
    {:chart       {:type :column}
     :tooltip     {:outside         true
                   :useHtml         true
                   :backgroundColor (theme/color [:page-bg :bg])
                   :style           {:opacity         1
                                     :backgroundColor (theme/color [:page-bg :bg])}
                   :enabled         false}
     :colors      [(color [:lp-report :chart :column :default])]
     :legend      {:enabled false}
     :plotOptions {:series {:colorByPoint true
                            :dataLabels   {:enabled true
                                           :style   {:color       "contrast"
                                                     :textOutline false}
                                           :format  "{y:.1f}"}}}
     :xAxis       {:categories    labels
                   :lineColor     (theme/color [:chart :x-axis :line])
                   :gridLineColor (theme/color [:chart :x-axis :grid-line])}
     :yAxis       [{:title         nil
                    :labels        {:format "{text} M€"}
                    :lineColor     (theme/color [:chart :y-axis :line])
                    :gridLineColor (theme/color [:chart :y-axis :grid-line])}]
     :series      [{:name "Gross investment at cost & valuation"
                    :data data}]}))

(defn- noi
  [{:keys [labels] :as values}]
  {:chart       {:zoomType "xy"}
   :xAxis       [{:categories    labels
                  :title         false
                  :lineColor     (theme/color [:chart :x-axis :line])
                  :gridLineColor (theme/color [:chart :x-axis :grid-line])}]
   :yAxis       [{:title         {:text "NOI"}
                  :labels        {:formatter chart-utils/axis-meur-formatter-1-decimal}
                  :softMin       0
                  :softMax       0
                  :lineColor     (theme/color [:chart :y-axis :line])
                  :gridLineColor (theme/color [:chart :y-axis :grid-line])}
                 {:title         {:text "Annualized net yield on FV"}
                  :opposite      true
                  :labels        {:format "{text} %"}
                  :softMin       0
                  :softMax       0
                  :lineColor     (theme/color [:chart :y-axis :line])
                  :gridLineColor (theme/color [:chart :y-axis :grid-line])}]
   :colors      (or (util/custom-colors)
                    (util/gradient-colors [:lp-report :chart :bar :gradient-start]
                                          [:lp-report :chart :bar :gradient-end]
                                          2))
   :tooltip     {:outside         true
                 :useHtml         true
                 :backgroundColor (theme/color [:page-bg :bg])
                 :style           {:opacity         1
                                   :backgroundColor (theme/color [:page-bg :bg])}
                 :shared          true}
   :plotOptions {:line   {:marker {:enabled false}}
                 :series {:lineWidth  3
                          :dataLabels {:style {:color       "contrast"
                                               :textOutline false}}}}
   :series      [{:name    "NOI"
                  :type    :column
                  :tooltip {:outside         true
                            :useHtml         true
                            :backgroundColor (theme/color [:page-bg :bg])
                            :style           {:opacity         1
                                              :backgroundColor (theme/color [:page-bg :bg])}
                            :pointFormatter  chart-utils/meur-formatter-divide-m}
                  :data    (:noi values)}
                 {:name    "Annualized net yield on FV"
                  :type    :line
                  :tooltip {:outside         true
                            :useHtml         true
                            :backgroundColor (theme/color [:page-bg :bg])
                            :style           {:opacity         1
                                              :backgroundColor (theme/color [:page-bg :bg])}
                            :pointFormatter  chart-utils/pct-formatter}
                  :data    (map #(* 100 %) (:annualized-net-yield-on-fv values))
                  :yAxis   1}]})

(defn ->ui-data
  [{:keys [dynamic-data report-data map-topo] :as props}]
  {:holding-name                     (get-in report-data [:holding-name])
   :reference-date                   (get-in report-data [:reference-date])
   :report-id                        (:report-id props)
   :investment-rationale-text        (report-ctl/rich-content props :investment-rationale-text)
   :business-plan-text               (report-ctl/rich-content props :business-plan-text)
   :access-management-update-text    (report-ctl/rich-content props :access-management-update-text)
   :esg-text                         (report-ctl/rich-content props :esg-text)
   :overview                         (report-ctl/with-footnote
                                      props
                                      :overview-footnotes
                                      (get-in report-data [:basic-information-primary]))
   :map-data                         (report-ctl/with-footnote
                                      props
                                      :map-data-footnotes
                                      (portfolio-locations map-topo (get-in report-data [:chart-data-map])))
   :investment-at-cost-and-valuation (->> (get-in report-data [:chart-data-valuation])
                                          (report-ctl/with-footnote props :investment-at-cost-and-valuation-footnotes)
                                          (report-ctl/with-editable-charts
                                           (assoc props :fixed-columns? true :fixed-labels? true)
                                           :investment-at-cost-and-valuation
                                           investment-at-cost-and-valuation
                                           config->editable-data-investment-at-cost-valuation
                                           editable-data->raw-data-investment-at-cost-valuation))

   :noi                              (->>
                                      (get-in report-data [:chart-data-noi])
                                      (report-ctl/with-footnote props :noi-footnotes)
                                      (report-ctl/with-editable-charts
                                       props
                                       :noi
                                       noi
                                       config->editable-data-noi
                                       editable-data->raw-data-noi))})
